import { getAddOns } from "common";
import { map, maxBy, minBy, orderBy } from "lodash";
import { CatalogItem, Experience, Package, PackageType, Product, ProductObject, PurchaseRule, UnitType } from "types";
import { getCatalogItemsByUnitType } from "./Catalog";
import { getExperienceMax, getExperienceMin } from "./Experience";

export const isVariableDemographicsPackage = (product?: Package | Product) => {
    if (product?.object === ProductObject.PARTNER_PACKAGE) {
        return true;
    }

    return (
        product?.object === ProductObject.PACKAGE &&
        "type" in product &&
        product.type === PackageType.DEMOGRAPHICS_VARIES
    );
};

export const isSharedDemographicsPackage = (product?: Package | Product) => {
    if (product?.object === ProductObject.PARTNER_PACKAGE) {
        return false;
    }

    if (product?.object === ProductObject.PACKAGE) {
        if ("type" in product) {
            return product.type === PackageType.DEMOGRAPHICS_SHARED;
        }

        // default case if no type is set in Package
        return true;
    }

    return false;
};

/**
 * Retrieves the all demographic catalog from a given package. if experienceId is passed, filter only the demographic catalog for that experience
 */
export const getPackageDemographicCatalog = (pkg: Package, experienceId?: ID) => {
    const demographics = pkg.catalog?.items?.filter((catalogItem) => {
        if (experienceId && isVariableDemographicsPackage(pkg)) {
            return catalogItem.unitType === UnitType.DEMOGRAPHIC && catalogItem.subProduct?.id === experienceId;
        }

        return catalogItem.unitType === UnitType.DEMOGRAPHIC;
    });

    return demographics ?? [];
};

export const getPackageAddonCatalog = (pkg: Package) => {
    const addOns = pkg.catalog?.items?.filter((catalogItem) => {
        return catalogItem.unitType === UnitType.ADDON;
    });
    return addOns ?? [];
};

export const getPackageProductIdsFromPurchaseRules = (purchaseRules: PurchaseRule[]): string[] => {
    const productIds: string[] = [];
    if (purchaseRules) {
        for (const purchaseRule of purchaseRules) {
            if (purchaseRule) {
                productIds.push(...getPackageProductIdsFromPurchaseRule(purchaseRule));
            }
        }
    }

    return productIds;
};

export const getPackageProductIdsFromPurchaseRule = (purchaseRule: PurchaseRule): string[] => {
    const productIds: string[] = [];
    const { filter } = purchaseRule;

    // This is to handle migrated packages with only one filter
    if (filter && filter.object === "all_filter" && filter.field === "purchase.items.product.id") {
        productIds.push(...(filter.operand as string[]));
    } else if (filter && (filter.object === "or_filter" || filter.object === "and_filter")) {
        for (const filterType of filter.filters) {
            if (filterType.object === "all_filter" && filterType.field === "purchase.items.product.id") {
                productIds.push(...(filterType.operand as string[]));
            }
        }
    }

    return productIds;
};

export const getPackageAddonCatalogs = (pkg: Package) => {
    return pkg.catalog.items.filter((catalogItem: CatalogItem) => {
        return catalogItem.unitType === UnitType.ADDON || catalogItem.unitType === UnitType.PICKUP;
    });
};

export const getHighestDemographicPrice = (pkg: Package) => {
    const highestPrice = maxBy(
        getPackageDemographicCatalog(pkg),
        (item: CatalogItem) => item.prices?.price?.max ?? null,
    );
    return highestPrice?.prices?.price?.max ?? null;
};

export const getLowestDemographicPrice = (pkg: Package, experiences?: Experience[]) => {
    if (isVariableDemographicsPackage(pkg) && experiences) {
        return getSumOfAllLowestDemographicPriceInExperiences(pkg, experiences);
    }

    const lowestPrice = minBy(
        getPackageDemographicCatalog(pkg),
        (item: CatalogItem) => item.prices?.price?.min ?? null,
    );
    return lowestPrice?.prices?.price?.min ?? null;
};

export const getSumOfAllLowestDemographicPriceInExperiences = (pkg: Package, experiences: Experience[]) => {
    const productIds = getProductIdsInPackage(pkg);
    const matches = experiences.filter((experience) => productIds.includes(experience.id));
    return matches.reduce((total, experience) => total + getExperienceMinPrice(experience), 0);
};

export const getExperienceMinPrice = (experience: Experience) => {
    return (
        minBy(
            getCatalogItemsByUnitType(experience.catalog.items, UnitType.DEMOGRAPHIC),
            (items) => items.prices?.price?.min ?? null,
        )?.prices?.price?.min ?? 0
    );
};

export const hasPriceRange = (pkg: Package) => {
    const lowestPrice = getLowestDemographicPrice(pkg);
    const highestPrice = getHighestDemographicPrice(pkg);
    return highestPrice !== lowestPrice;
};

const getMaxDemographicQtyForPackage = (pkg: Package, experiences: Experience[]) => {
    const productIds = getProductIdsInPackage(pkg);
    const matches = experiences.filter((experience) => productIds.includes(experience.id));
    const maxQtyExperience = minBy(matches, (experience) => getExperienceMax(experience) ?? null);

    return maxQtyExperience ? getExperienceMax(maxQtyExperience) : null;
};

const getMinDemographicQtyForPackage = (pkg: Package, experiences: Experience[]) => {
    const productIds = getProductIdsInPackage(pkg);
    const matches = experiences.filter((experience) => productIds.includes(experience.id));
    const minQtyExperience = maxBy(matches, (experience) => getExperienceMin(experience) ?? null);

    return minQtyExperience ? getExperienceMin(minQtyExperience) : null;
};

export const getMinMaxForPackage = (pkg: Package, experiences: Experience[]) => {
    return [getMinDemographicQtyForPackage(pkg, experiences), getMaxDemographicQtyForPackage(pkg, experiences)];
};

export const getProductIdsInPackage = (pkg: Package): ID[] => {
    return map(orderBy(pkg.selectedProducts, ["sequence"], ["asc"]), (selectedProduct) => selectedProduct.product.id);
};

export const getExpandedExperiencesInPackage = (pkg: Package): Experience[] => {
    return [...pkg.selectedProducts]
        .sort((a, b) => a.sequence - b.sequence)
        .map((selectedProduct) => selectedProduct.product)
        .filter((product): product is Experience => "object" in product && product.object === ProductObject.EXPERIENCE);
};

export const hasSelectedProductsExpanded = (pkg: Package) => {
    return pkg.selectedProducts?.[0] && "object" in pkg.selectedProducts[0].product;
};

export const getExperiencesInPackage = (pkg: Package, experiences: Experience[]) => {
    const experienceIds = getProductIdsInPackage(pkg);
    const filteredExperiences: Experience[] = [];

    for (const id of experienceIds) {
        const experience = experiences.find((experience) => experience.id === id);
        if (experience) {
            filteredExperiences.push(experience);
        }
    }

    return filteredExperiences;
};

export const getPackageAddOnsInExperience = (pkg: Package, experience: Experience) => {
    const packageCatalogItems = getPackageAddonCatalogs(pkg);
    const experienceCatalogItems = getAddOns(experience);
    const catalogItems = [];

    for (const experienceCatalogItem of experienceCatalogItems) {
        for (const packageCatalogItem of packageCatalogItems) {
            if (
                packageCatalogItem.parent?.id === experienceCatalogItem.parent?.id &&
                packageCatalogItem.subProduct?.id === experienceCatalogItem.product?.id
            ) {
                const catalogItem = {
                    ...packageCatalogItem,
                    prices: { ...packageCatalogItem.prices, price: experienceCatalogItem.prices?.price },
                };

                catalogItems.push(catalogItem);
            }
        }
    }

    return catalogItems;
};

export const isExperienceExistsInPackage = (pkg: Package, id: string) => {
    return pkg.selectedProducts.some((selectedProduct) => selectedProduct.product.id === id);
};

export const getPackageIfExperienceExists = (pkgs: Package[], id: string) => {
    return pkgs.find((pkg) => isExperienceExistsInPackage(pkg, id));
};

export const isPartnerPackage = (pkg: Package) => {
    return pkg.object === ProductObject.PARTNER_PACKAGE;
};
